// Path: src/components/aols/olsme/olsme.tsx
// Updated to fix JSX closing tag in TimerFrame.
// Previous user update: fix isSessionActive reference in OlsmeMonitor, address MIME type issues, and optimize for macOS 11
// 06/04/25 (user's timestamp) / Corrected on 06/04/25 (current interaction)
// Path: src/components/aols/olsme/olsme.tsx
// Added Loader component above OlsmeMonitor, visible during active session.

import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { Layout, Button, Typography, Row, Col, Card, Breadcrumb, InputNumber, Select, Form } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  BulbOutlined, TeamOutlined, RiseOutlined, GlobalOutlined,
  UserOutlined, BankOutlined, HomeOutlined
} from "@ant-design/icons";
import logo from "/logo.jpg";
import useOlsmeTimer from "../../../hooks/useOlsmeTimer"; // Existing hook for timer data
import { publicImages } from "../../auth/constants/PublicImg";
import { OLSME_SUBSCRIPTION_DATA, calculateOlsmePrices, CalculatedPrices } from "./constants/subscriptionData"; // Using user's relative path
import Loader from "../../../components/Loader"; // Added Loader import
import "./olsme.css";

const { Title, Paragraph } = Typography;
const { Option } = Select;

interface Benefit { icon: React.ReactNode; title: string; description: string; }
interface Service { icon: React.ReactNode; title: string; description: string; link: string; }

// --- Styled Components (Copied from your provided file) ---
const HeroSection = styled.section`
  position: relative;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%), url("https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2Folsme2024.png?alt=media&token=083c310d-0045-48c8-a516-786286b3cc1e") no-repeat center center;
  background-size: cover;
  color: #fff;
  text-align: center;
  padding: 80px 20px;
  border-bottom: 2px solid #ffaa00;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  @media (max-width: 768px) {
    padding: 40px 10px;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1;
`;

const HeroContent = styled.div`position: relative; z-index: 2;`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin: 20px 0;
  flex-wrap: wrap;
`;

const NavButton = styled(Button)`
  background: #ffcc00;
  border: none;
  color: #0427f4;
  font-size: 16px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  transition: background 0.3s ease, transform 0.3s ease;
  &:hover { background: #ffaa00; color: #0427f4; transform: scale(1.05); }
  a { color: inherit; text-decoration: none; }
`;

const IntroSectionStyled = styled.div`
  background: linear-gradient(135deg, #1a1a1a, #2a2a2a);
  border-radius: 12px;
  padding: 30px;
  margin: 15px 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  text-align: left;
  color: #fff;
  .content-text { color: #fff; font-size: 16px; line-height: 1.8em; margin-bottom: 20px; }
  .subsection-title { color: #ffcc00; }
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const SharedCardStyle = `
  background: linear-gradient(135deg, #1a1a1a, #2a2a2a);
  border: 1px solid #ffaa00;
  border-radius: 12px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  animation: fadeIn 1s ease-in-out;
  color: #fff;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 24px rgba(255, 204, 0, 0.4);
  }
  .ant-card-body { text-align: center; padding: 25px; color: #fff; }
  .anticon { font-size: 48px; color: #ffcc00; margin-bottom: 20px; }
  .ant-typography, .ant-typography p, h4 { color: #fff; }
`;
const BenefitCardStyled = styled(Card)`${SharedCardStyle} .ant-card-body { justify-content: center; }`;
const ServiceCardStyled = styled(Card)`${SharedCardStyle} .ant-card-body { flex-grow: 1; justify-content: space-between; }`;
const ServiceCTA = styled.div`text-align: center; margin-top: 20px;`;

const MonitorContainer = styled.div`
  background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const MonitorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
`;

const MonitorTitle = styled(Title)`color: #ffaa00 !important; margin: 0;`;

const MonitorContent = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 20px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const MonitorCard = styled(Card)`
  background: #333;
  border: 1px solid #ffaa00;
  border-radius: 8px;
  color: #fff;
  text-align: center;
  padding: 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  .content-text { margin-bottom: 10px; }
  .custom-button { margin-top: 10px; font-size: 14px; padding: 4px 8px; }
`;

const TimerSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #333;
  border: 1px solid #ffaa00;
  border-radius: 8px;
  padding: 15px;
  flex: 1;
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const JoinButton = styled(Button)`
  position: absolute;
  top: 5px;
  left: 5px;
  font-size: 10px;
  padding: 2px 4px;
  background-color: #1890ff;
  border-color: #1890ff;
  color: white;
  transition: all 0.3s ease;
  z-index: 1;
  &:hover, &:focus { background-color: #40a9ff; border-color: #40a9ff; color: white; transform: scale(1.05); }
  a { color: white; text-decoration: none; }
  @media (max-width: 768px) { font-size: 8px; padding: 1px 3px; }
`;

const TimerFrame = styled.div`
  background: #000000;
  border: 1px solid #ffffff;
  border-radius: 6px;
  padding: 4px 8px;
  margin: 4px 0;
  z-index: 2;
`;

const TimerText = styled(Paragraph)`
  color: #fff;
  font-size: 16px;
  margin: 0;
  @media (max-width: 768px) { font-size: 14px; }
`;

const PreEvaluationSectionStyled = styled.div`
  background: linear-gradient(135deg, #1a1a1a, #2a2a2a);
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  color: #fff;
  .ant-form-item-label > label { color: #fff; }
  .ant-input-number, .ant-select-selector { background-color: #333; color: #fff; border: 1px solid #ffaa00; }
  .ant-input-number-input, .ant-select-selector .ant-select-selection-item, .ant-select-arrow { color: #fff; }
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const FormContainer = styled.div`max-width: 600px; margin: 0 auto;`;

const CalculateButton = styled(Button)`
  background: #ffcc00;
  border: none;
  color: #0427f4;
  font-size: 16px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  transition: background 0.3s ease, transform 0.3s ease;
  margin-top: 10px;
  &:hover { background: #ffaa00; color: #0427f4; transform: scale(1.05); }
`;

const ResultContainer = styled.div`
  margin-top: 20px;
  text-align: center;
  background: rgba(0,0,0,0.7);
  border: 1px solid #ffaa00;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ResultItem = styled.div<{ backgroundColor?: string; fontSize?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  background: ${(props) => props.backgroundColor || "transparent"};
  border: 1px solid #ffaa00;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: ${(props) => props.fontSize || "16px"};
  color: #fff;
`;

const ResultText = styled(Paragraph)`color: #ffcc00 !important; font-size: 16px; margin: 0;`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const OlsmeLogo = styled.img`
  max-width: 150px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(255,204,0,0.3);
  transition: transform 0.3s ease;
  &:hover { transform: scale(1.1); }
  @media (max-width: 768px) { max-width: 120px; }
`;

const CTASectionStyled = styled.section`
  background: #1a1a1a;
  padding: 40px 20px;
  text-align: center;
  border-top: 2px solid #ffaa00;
  border-bottom: 2px solid #ffaa00;
  @media (max-width: 768px) {
    padding: 20px 10px;
  }
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;
  .ant-breadcrumb a { color: #ffcc00; transition: color 0.3s ease; }
  .ant-breadcrumb a:hover { color: #ffaa00; }
  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb { font-size: 12px; }
  }
`;

// --- Sub-Components ---

const OlsmeHero: React.FC = () => (
  <HeroSection>
    <HeroOverlay />
    <HeroContent>
      <Link to="/" aria-label="Go to OLSME homepage">
        <img className="logo" src={logo} alt="OneLightSystem OLS Logo" width="200" loading="lazy" />
      </Link>
      <Title level={1} className="section-title">ONELIGHTSYSTEM MEDITATIVE ENERGY olsme</Title>
      <ButtonRow>
        <NavButton><Link to="/lightScience">OneLightSystem</Link></NavButton>
        <NavButton><Link to="/OLSaeo">OLS iee.aeo</Link></NavButton>
        <NavButton><Link to="/OLSStudentProspect">Join OLS</Link></NavButton>
        <NavButton><Link to="/OlsmePrivacy">OLSME Privacy</Link></NavButton>
      </ButtonRow>
      <Paragraph className="content-text" style={{ maxWidth: "800px", margin: "0 auto", fontSize: "18px" }}>
        Discover the transformative power of meditative energy with OLSME, an accessible extension of OneLightSystem’s Sun Light Meditation. Designed to synchronize you with Source Energy, OLSME offers a radiant journey toward awareness illumination and global connectivity. Join OLS at /OLSStudentProspect to begin your luminous path today!
      </Paragraph>
    </HeroContent>
  </HeroSection>
);

const OlsmeIntro: React.FC = () => (
  <IntroSectionStyled>
    <Title level={3} className="subsection-title">What is OLSME?</Title>
    <Paragraph className="content-text">
      OLSME (OneLightSystem Meditative Energy) is an accessible extension of OneLightSystem’s meditation practices, enabling users to attune to the systematic natural Source Presence. This innovative approach fosters a deeper connection with Source Energy, enhancing personal growth and awareness.
    </Paragraph>
    <Paragraph className="content-text">
      Developed by OLS iense, OLSME was initially created for OLS aste (master) practitioners and students. Now, it’s available to the public, offering a quick attunement process to develop greater self-awareness and meditative proficiency.
    </Paragraph>
    <Paragraph className="content-text">
      How it works: The OLSMeditator acts as a driver, guiding the OLSME user—passenger—through meditative rest sessions. These sessions enhance personal energy accumulation and form the Inner Equity Formation (IEF), paving the way for a more profound meditation experience.
    </Paragraph>
  </IntroSectionStyled>
);

const servicesData: Service[] = [
  { icon: <UserOutlined />, title: "OLSME Subscription", description: "A public verified portfolio for individuals eager to join OLSME and unlock the benefits of meditative energy through structured Sun Light Meditation sessions.", link: "/olsmeSub" },
  { icon: <BankOutlined />, title: "OLSME Corporates", description: "Tailored training packages designed for corporates and governmental employees to integrate Sun Light Meditation into workplace wellness and productivity.", link: "/register/olsmeregister" },
];

const OlsmeServices: React.FC = () => (
  <article className="feature-card">
    <Title level={3} className="subsection-title">Explore OLSME Services</Title>
    <Row gutter={[16, 16]} justify="center">
      {servicesData.map((service, index) => (
        <Col xs={24} sm={12} md={12} key={index}>
          <ServiceCardStyled>
            {service.icon}
            <Title level={4} style={{ color: "#ffcc00", marginBottom: "15px", fontSize: "20px" }}>{service.title}</Title>
            <Paragraph className="content-text" style={{ fontSize: "16px", lineHeight: "1.6em" }}>{service.description}</Paragraph>
            <ServiceCTA>
              <Button className="custom-button" size="large"><Link to={service.link}>Register</Link></Button>
            </ServiceCTA>
          </ServiceCardStyled>
        </Col>
      ))}
    </Row>
  </article>
);

const OlsmeTimerDisplay: React.FC = () => {
  const { timer, sessionLabel, isSessionActive } = useOlsmeTimer();
  return (
    <TimerSection>
      <JoinButton className="custom-button"><Link to="/register">Join OLSME Now</Link></JoinButton>
      {isSessionActive && <span className="session-notice" aria-label="OLSME session in progress">olsme in progress</span>}
      <TimerFrame><TimerText>{timer}</TimerText></TimerFrame>
      <TimerText>{sessionLabel}</TimerText>
    </TimerSection>
  );
};

const OlsmeMonitor: React.FC = () => {
  const { isSessionActive: monitorIsSessionActive } = useOlsmeTimer(); 
  // Renamed to avoid conflict if Loader also used useOlsmeTimer directly in future or if passed as prop
  return (
    <MonitorContainer>
      <MonitorHeader><MonitorTitle level={3}>OLSME Monitor</MonitorTitle></MonitorHeader>
      <MonitorContent>
        <MonitorCard>
          <Paragraph className="content-text">Scheduled for your next OLSME session. Prepare to align with Source Energy!</Paragraph>
          <Button className="custom-button"><Link to="/schedule">Schedule</Link></Button>
        </MonitorCard>
        <MonitorCard>
          <Paragraph className="content-text">{monitorIsSessionActive ? "OLSME session in progress—stay connected!" : "OLSME begins in 5 minutes. You are ready!"}</Paragraph>
          <Button className="custom-button"><Link to="/register">Confirm</Link></Button>
        </MonitorCard>
        <OlsmeTimerDisplay /> 
      </MonitorContent>
    </MonitorContainer>
  );
};

const benefitsData: Benefit[] = [
  { icon: <BulbOutlined />, title: "Awareness Illumination", description: "Unlock your potential with OLSME’s Sun Light Meditation sessions, designed to enhance personal growth and synchronize you with Source Energy for a radiant inner light." },
  { icon: <TeamOutlined />, title: "Natural Coherence Synchronization", description: "Experience a natural energetic rest and boost, supported by eight years of systematic OLSM Sun Light Meditation practices for holistic well-being." },
  { icon: <RiseOutlined />, title: "Energy Accumulation", description: "Develop automatic access to Source Energy through consistent OLSME sessions, boosting self-awareness and sustaining vitality over time." },
  { icon: <GlobalOutlined />, title: "Global Reach Natural G-Connectivity", description: "Attune with Source Energy from anywhere in the world, leveraging OLSME’s global network to enhance your meditative experience and awareness." },
];

const OlsmeBenefits: React.FC = () => (
  <article className="feature-card">
    <Title level={3} className="subsection-title">Benefits of OLSME</Title>
    <Row gutter={[16, 16]} justify="center">
      {benefitsData.map((benefit, index) => (
        <Col xs={24} sm={12} md={6} key={index}>
          <BenefitCardStyled>
            {benefit.icon}
            <Title level={4} style={{ color: "#ffcc00", marginBottom: "15px", fontSize: "20px" }}>{benefit.title}</Title>
            <Paragraph className="content-text" style={{ fontSize: "16px", lineHeight: "1.6em" }}>{benefit.description}</Paragraph>
          </BenefitCardStyled>
        </Col>
      ))}
    </Row>
  </article>
);

const OlsmePreEvaluationForm: React.FC = () => {
  const [userAge, setUserAge] = useState<number | null>(33);
  const [locationSize, setLocationSize] = useState<string | null>("1000000");
  const [prices, setPrices] = useState<CalculatedPrices>({ subscriptionPrice: null, promoPrice: null, projectedPrice: null });

  const handleCalculate = useCallback(() => {
    const calculated = calculateOlsmePrices(userAge, locationSize, OLSME_SUBSCRIPTION_DATA);
    setPrices(calculated);
  }, [userAge, locationSize]);

  useEffect(() => {
    handleCalculate();
  }, [handleCalculate]);

  return (
    <article className="feature-card">
      <PreEvaluationSectionStyled>
        <Title level={3} className="subsection-title">Pre-Evaluation for US Subscription Price</Title>
        <FormContainer>
          <Form layout="vertical">
            <Form.Item label="User Age" className="content-text">
              <InputNumber min={0} max={120} value={userAge} onChange={(value) => setUserAge(typeof value === 'number' ? value : null)} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Location Size (Population)" className="content-text">
              <Select value={locationSize} onChange={(value) => setLocationSize(value)} style={{ width: "100%" }} placeholder="Select location size">
                <Option value="1000">1,000</Option>
                <Option value="10000">10,000</Option>
                <Option value="100000">100,000</Option>
                <Option value="1000000">1,000,000</Option>
                <Option value="10000000">10,000,000+</Option>
              </Select>
            </Form.Item>
            <CalculateButton onClick={handleCalculate}>Calculate</CalculateButton>
          </Form>
          {(prices.subscriptionPrice !== null || prices.promoPrice !== null || prices.projectedPrice !== null) && (
            <ResultContainer>
              <ResultItem backgroundColor="#333">2025 Subscription: {prices.subscriptionPrice !== null ? `$${prices.subscriptionPrice.toFixed(2)}` : "N/A"}</ResultItem>
              <ResultItem backgroundColor="#000" fontSize="18px">2025 Promo: {prices.promoPrice !== null ? `$${prices.promoPrice.toFixed(2)}` : "Not Available"}</ResultItem>
              <ResultText>2026 Projected Monthly: {prices.projectedPrice !== null ? `$${prices.projectedPrice.toFixed(2)}` : "N/A"}</ResultText>
            </ResultContainer>
          )}
        </FormContainer>
      </PreEvaluationSectionStyled>
    </article>
  );
};

const OlsmeWhyChoose: React.FC = () => (
  <article className="feature-card">
    <Title level={3} className="subsection-title">Why Choose OLSME?</Title>
    <Paragraph className="content-text">
      Learn new professional skills and develop next-level abilities through Sun Light Meditation! OLSME empowers you with tools to enhance your meditative practice.
    </Paragraph>
    <Paragraph className="content-text">
      Experience natural coherent energy synchronization, fostering a harmonious connection with Source Energy for a radiant and balanced life.
    </Paragraph>
    <Paragraph className="content-text">
      OLSME’s real natural light data signals accumulation equity assets program allows users to develop an automatic access amount of energy over time, strengthening your inner equity formation.
    </Paragraph>
    <LogoSection>
      <Paragraph className="content-text">2891.ONELIGHTSYSTEM OLS iee.aeo</Paragraph>
      <OlsmeLogo src={publicImages.getImageById(9).url} alt={publicImages.getImageById(9).alt} loading="lazy" />
    </LogoSection>
  </article>
);

const OlsmeCTA: React.FC = () => (
  <CTASectionStyled>
    <Title level={2} className="section-title">Ready to Join the Light Civilization?</Title>
    <Paragraph className="content-text" style={{ maxWidth: "600px", margin: "0 auto 20px" }}>
      Start your journey with OLSME and unlock the power of meditative energy in the digital age. Join our global Sun Light Civilization today!
    </Paragraph>
    <Button className="custom-button" size="large"><Link to="/register">Get Started Today</Link></Button>
    <Paragraph className="content-text" style={{ marginTop: "20px" }}>
      Join OLS at /OLSStudentProspect to embrace this luminous path and connect with a community dedicated to empowerment and growth!
    </Paragraph>
  </CTASectionStyled>
);

// --- Main Olsme Component ---
const Olsme: React.FC = () => {
  // Get isSessionActive from the hook to control the Loader visibility
  const { isSessionActive } = useOlsmeTimer();

  const structuredData = [ 
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "OneLightSystem OLS",
      "url": "https://www.olsme.com",
      "subOrganization": {
        "@type": "EducationalOrganization",
        "name": "OLSME",
        "url": "https://www.olsme.com/olsme",
        "description": "OLSME offers Sun Light Meditation programs to connect users with Source Energy for inner illumination.",
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "OLSME Subscription",
      "description": "Join OLSME to access Sun Light Meditation sessions for personal growth and global connectivity.",
      "offers": {
        "@type": "Offer",
        "url": "https://www.olsme.com/olsmeSub",
        "priceCurrency": "USD",
        "price": "33.00", 
        "availability": "https://schema.org/OnlineOnly"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.olsme.com" },
        { "@type": "ListItem", "position": 2, "name": "OLSME", "item": "https://www.olsme.com/olsme" }
      ]
    }
  ];

  return (
    <React.Fragment>
      <Helmet>
        <title>OLSME - OneLightSystem Meditative Energy | Sun Light Meditation</title>
        <meta name="description" content="Discover OLSME’s Sun Light Meditation programs to unlock meditative energy and join a radiant Sun Light Civilization. Register at olsme.com/OLSStudentProspect!" />
        <meta name="keywords" content="OLSME, Sun Light Meditation, meditative energy, OneLightSystem OLS, join OLS, spiritual growth, global connectivity" />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="OLSME - OneLightSystem Meditative Energy | Sun Light Meditation" />
        <meta property="og:description" content="Join OLSME to experience Sun Light Meditation and connect with Source Energy. Start your journey at olsme.com/OLSStudentProspect!" />
        <meta property="og:url" content="https://www.olsme.com/olsme" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={logo} /> {/* Use imported logo for consistency */}
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <BreadcrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Item><Link to="/"><HomeOutlined /> Home</Link></Breadcrumb.Item>
          <Breadcrumb.Item>OLSME</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbWrapper>

      <OlsmeHero />
      <section className="content-section">
        <div className="content">
          <OlsmeIntro />
          <OlsmeServices />
          {/* Conditionally render Loader above OlsmeMonitor */}
          {isSessionActive && <Loader />}
          <OlsmeMonitor />
          <OlsmeBenefits />
          <OlsmePreEvaluationForm />
          <OlsmeWhyChoose />
        </div>
      </section>
      <OlsmeCTA />
    </React.Fragment>
  );
};

export default Olsme;